import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ArrowLeft, Plus, Edit, Trash2, DollarSign, Info, Eye, EyeOff, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademyRateTableManagement({ user }) {
  const [rateTables, setRateTables] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  
  // Form fields
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  
  // 3-tier membership discount system with toggles
  const [clubMemberEnabled, setClubMemberEnabled] = useState(true);
  const [clubMemberValue, setClubMemberValue] = useState("15");
  const [sectionMemberEnabled, setSectionMemberEnabled] = useState(true);
  const [sectionMemberValue, setSectionMemberValue] = useState("20");
  const [digitalMemberEnabled, setDigitalMemberEnabled] = useState(true);
  const [digitalMemberValue, setDigitalMemberValue] = useState("8");
  
  // Sibling discounts with toggles - array of { child_number, type, value, enabled }
  const [siblingDiscounts, setSiblingDiscounts] = useState([
    { child_number: 2, type: "percentage", value: "10", enabled: false },
    { child_number: 3, type: "percentage", value: "15", enabled: false }
  ]);
  
  // Session count discounts with toggles - array of { session_count, type, value, enabled }
  const [sessionDiscounts, setSessionDiscounts] = useState([
    { session_count: 2, type: "percentage", value: "5", enabled: false },
    { session_count: 3, type: "percentage", value: "10", enabled: false }
  ]);
  
  // Pricing mode: "progressive" (current) or "volume" (new)
  const [pricingMode, setPricingMode] = useState("progressive");
  
  // Discount stacking mode
  const [discountStackingMode, setDiscountStackingMode] = useState("highest_only");
  
  // Volume pricing tiers - array of { min_sessions, price_per_session }
  // price_per_session is the NON-MEMBER (base) price. Members get discount applied.
  const [volumePricingTiers, setVolumePricingTiers] = useState([
    { min_sessions: 1, price_per_session: "" },
    { min_sessions: 2, price_per_session: "" },
    { min_sessions: 3, price_per_session: "" }
  ]);

  // Max Capacity (Fixed Term) — Jun 2026. Parent always pays for
  // max_sessions_per_week × weeks × price regardless of picked count.
  const [maxCapacityPricePerSession, setMaxCapacityPricePerSession] = useState("");
  const [maxSessionsPerWeek, setMaxSessionsPerWeek] = useState("3");

  // Warning dialog for editing locked rate tables
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [pendingPayload, setPendingPayload] = useState(null);

  const isAdmin = ["convenor", "captain"].includes(user?.role);

  useEffect(() => {
    if (isAdmin) {
      fetchRateTables();
    }
  }, [isAdmin]);

  const fetchRateTables = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/academy/rate-tables`, { withCredentials: true });
      setRateTables(response.data);
    } catch (error) {
      console.error("Error fetching rate tables:", error);
      toast.error("Failed to load rate tables");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingTable(null);
    setName("");
    setNotes("");
    setClubMemberEnabled(true);
    setClubMemberValue("15");
    setSectionMemberEnabled(true);
    setSectionMemberValue("20");
    setDigitalMemberEnabled(true);
    setDigitalMemberValue("8");
    setSiblingDiscounts([
      { child_number: 2, type: "percentage", value: "10", enabled: false },
      { child_number: 3, type: "percentage", value: "15", enabled: false }
    ]);
    setSessionDiscounts([
      { session_count: 2, type: "percentage", value: "5", enabled: false },
      { session_count: 3, type: "percentage", value: "10", enabled: false },
      { session_count: 4, type: "percentage", value: "15", enabled: false },
      { session_count: 5, type: "percentage", value: "20", enabled: false }
    ]);
    setPricingMode("progressive");
    setDiscountStackingMode("highest_only");
    setVolumePricingTiers([
      { min_sessions: 1, price_per_session: "" },
      { min_sessions: 2, price_per_session: "" },
      { min_sessions: 3, price_per_session: "" }
    ]);
    setMaxCapacityPricePerSession("");
    setMaxSessionsPerWeek("3");
    setShowDialog(true);
  };

  const openEditDialog = (table) => {
    setEditingTable(table);
    setName(table.name);
    setNotes(table.notes || "");
    
    // Load membership discounts with toggle states
    const clubVal = table.membership_discounts?.club_member?.value;
    const sectionVal = table.membership_discounts?.section_member?.value;
    const digitalVal = table.membership_discounts?.digital_member?.value;
    
    setClubMemberEnabled(clubVal > 0);
    setClubMemberValue(clubVal > 0 ? clubVal.toString() : "15");
    setSectionMemberEnabled(sectionVal > 0);
    setSectionMemberValue(sectionVal > 0 ? sectionVal.toString() : "20");
    setDigitalMemberEnabled(digitalVal > 0);
    setDigitalMemberValue(digitalVal > 0 ? digitalVal.toString() : "8");
    
    // Load sibling discounts with toggle state
    const defaultSiblingDiscounts = [
      { child_number: 2, type: "percentage", value: "10", enabled: false },
      { child_number: 3, type: "percentage", value: "15", enabled: false }
    ];
    
    if (table.sibling_discounts && table.sibling_discounts.length > 0) {
      // Map existing discounts and mark as enabled
      const loadedSiblings = defaultSiblingDiscounts.map(defaultD => {
        const existing = table.sibling_discounts.find(d => d.child_number === defaultD.child_number);
        if (existing && existing.value > 0) {
          return { ...existing, value: existing.value.toString(), enabled: true };
        }
        return defaultD;
      });
      setSiblingDiscounts(loadedSiblings);
    } else {
      setSiblingDiscounts(defaultSiblingDiscounts);
    }
    
    // Load session discounts with toggle state
    const defaultSessionDiscounts = [
      { session_count: 2, type: "percentage", value: "5", enabled: false },
      { session_count: 3, type: "percentage", value: "10", enabled: false },
      { session_count: 4, type: "percentage", value: "15", enabled: false },
      { session_count: 5, type: "percentage", value: "20", enabled: false }
    ];
    
    if (table.session_count_discounts && table.session_count_discounts.length > 0) {
      const loadedSessions = defaultSessionDiscounts.map(defaultD => {
        const existing = table.session_count_discounts.find(d => d.session_count === defaultD.session_count);
        if (existing && existing.value > 0) {
          return { ...existing, value: existing.value.toString(), enabled: true };
        }
        return defaultD;
      });
      setSessionDiscounts(loadedSessions);
    } else {
      setSessionDiscounts(defaultSessionDiscounts);
    }
    
    // Load pricing mode and volume tiers
    setPricingMode(table.pricing_mode || "progressive");
    setDiscountStackingMode(table.discount_stacking_mode || "highest_only");
    setVolumePricingTiers(
      table.volume_pricing_tiers?.length > 0
        ? table.volume_pricing_tiers.map(t => ({ ...t, price_per_session: t.price_per_session?.toString() || "" }))
        : [
            { min_sessions: 1, price_per_session: "" },
            { min_sessions: 2, price_per_session: "" },
            { min_sessions: 3, price_per_session: "" }
          ]
    );

    // Load Max Capacity (Fixed Term) config
    setMaxCapacityPricePerSession(
      table.max_capacity_price_per_session != null
        ? String(table.max_capacity_price_per_session)
        : ""
    );
    setMaxSessionsPerWeek(
      table.max_sessions_per_week != null ? String(table.max_sessions_per_week) : "3"
    );
    
    setShowDialog(true);
  };

  const handleSubmit = async (forceEdit = false) => {
    try {
      // Validate
      if (!name.trim()) {
        toast.error("Rate table name is required");
        return;
      }

      // Prepare discount data (3-tier system) with toggles
      // If toggle is OFF, value is 0. If toggle is ON, use the entered value.
      const parseDiscount = (enabled, value, defaultVal) => {
        if (!enabled) return 0;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? defaultVal : parsed;
      };
      
      const membershipDiscounts = {
        club_member: { type: "percentage", value: parseDiscount(clubMemberEnabled, clubMemberValue, 15) },
        section_member: { type: "percentage", value: parseDiscount(sectionMemberEnabled, sectionMemberValue, 20) },
        digital_member: { type: "percentage", value: parseDiscount(digitalMemberEnabled, digitalMemberValue, 8) }
      };

      // Only include sibling discounts that are enabled
      const siblingData = siblingDiscounts
        .filter(d => d.enabled && d.value && parseFloat(d.value) > 0)
        .map(d => ({
          child_number: d.child_number,
          type: d.type,
          value: parseFloat(d.value)
        }));

      // Only include session discounts that are enabled
      const sessionData = sessionDiscounts
        .filter(d => d.enabled && d.value && parseFloat(d.value) > 0)
        .map(d => ({
          session_count: d.session_count,
          type: d.type,
          value: parseFloat(d.value)
        }));

      // Prepare volume pricing tiers - price_per_session is the NON-MEMBER (base) price
      const volumeTiersData = volumePricingTiers
        .filter(t => t.price_per_session && parseFloat(t.price_per_session) > 0)
        .map(t => ({
          min_sessions: t.min_sessions,
          price_per_session: parseFloat(t.price_per_session)
        }));

      const payload = {
        name,
        notes,
        membership_discounts: membershipDiscounts,
        sibling_discounts: siblingData,
        session_count_discounts: sessionData,
        pricing_mode: pricingMode,
        discount_stacking_mode: discountStackingMode,
        volume_pricing_tiers: volumeTiersData,
        max_capacity_price_per_session:
          pricingMode === "max_capacity" && maxCapacityPricePerSession
            ? parseFloat(maxCapacityPricePerSession)
            : null,
        max_sessions_per_week:
          pricingMode === "max_capacity" && maxSessionsPerWeek
            ? parseInt(maxSessionsPerWeek)
            : null,
        force_edit: forceEdit  // Pass force_edit flag
      };

      if (editingTable) {
        // Update
        const response = await axios.put(`${API}/academy/rate-tables/${editingTable.id}`, payload, { withCredentials: true });
        
        // Check if backend returned a warning (rate table is in use)
        if (response.data.warning && response.data.requires_confirmation) {
          setWarningMessage(response.data.message);
          setPendingPayload(payload);
          setShowWarningDialog(true);
          return;
        }
        
        toast.success("Rate table updated successfully");
      } else {
        // Create
        await axios.post(`${API}/academy/rate-tables`, payload, { withCredentials: true });
        toast.success("✅ Rate table created! Next: Go to Term Management to create a term and link it to this rate table.");
      }

      setShowDialog(false);
      fetchRateTables();
    } catch (error) {
      console.error("Error saving rate table:", error);
      toast.error(error.response?.data?.detail || "Failed to save rate table");
    }
  };

  // Handle confirmed force edit
  const handleConfirmForceEdit = async () => {
    setShowWarningDialog(false);
    if (pendingPayload) {
      await handleSubmit(true);  // Call with force_edit = true
      setPendingPayload(null);
    }
  };

  const handleDelete = async (tableId) => {
    if (!window.confirm("Are you sure you want to delete this rate table? This cannot be undone.")) {
      return;
    }

    try {
      await axios.delete(`${API}/academy/rate-tables/${tableId}`, { withCredentials: true });
      toast.success("Rate table deleted");
      fetchRateTables();
    } catch (error) {
      console.error("Error deleting rate table:", error);
      toast.error(error.response?.data?.detail || "Failed to delete rate table");
    }
  };

  // Toggle hide/show for rate table
  const handleToggleHide = async (tableId, hide) => {
    try {
      await axios.patch(
        `${API}/academy/rate-tables/${tableId}/visibility`,
        { is_hidden: hide },
        { withCredentials: true }
      );
      toast.success(hide ? "Rate table hidden from registration" : "Rate table is now visible");
      fetchRateTables();
    } catch (error) {
      console.error("Error toggling rate table visibility:", error);
      toast.error(error.response?.data?.detail || "Failed to update visibility");
    }
  };

  const updateSiblingDiscount = (index, field, value) => {
    const updated = [...siblingDiscounts];
    updated[index][field] = value;
    setSiblingDiscounts(updated);
  };

  const updateSessionDiscount = (index, field, value) => {
    const updated = [...sessionDiscounts];
    updated[index][field] = value;
    setSessionDiscounts(updated);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] p-6">
        <Card className="max-w-2xl mx-auto p-8 text-center">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h2>
          <p className="text-slate-400">Only administrators can manage rate tables.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/academy">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-3xl font-bold text-white">Rate Table Management</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/academy/term-management">
              <Button variant="outline" className="border-green-600 text-emerald-400 hover:bg-emerald-500/10">
                Next: Create Term →
              </Button>
            </Link>
            <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Rate Table
            </Button>
          </div>
        </div>

        {/* Info Card */}
        <Card className="mb-6 p-4 bg-blue-500/10 border-blue-500/30">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-300">
              <p className="font-semibold mb-2">📋 Academy Setup Flow:</p>
              <ol className="space-y-1 ml-4 list-decimal">
                <li><strong>Create Rate Table</strong> - Define discount percentages (you are here)</li>
                <li><strong>Create Term</strong> - Go to Term Management → Link to rate table</li>
                <li><strong>Add Sessions</strong> - Select term → Add sessions with BASE PRICES</li>
                <li><strong>Finalize Term</strong> - Lock pricing → Term becomes available for registration</li>
                <li><strong>Parents Register</strong> - See calculated fees with discounts applied</li>
              </ol>
              <p className="mt-2 text-xs">💡 Rate tables store discount percentages. Base session prices are set in Step 3.</p>
            </div>
          </div>
        </Card>

        {/* Rate Tables List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-400">Loading rate tables...</p>
          </div>
        ) : rateTables.length === 0 ? (
          <Card className="p-8 text-center">
            <DollarSign className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Rate Tables Yet</h3>
            <p className="text-slate-400 mb-4">Create your first rate table to start managing academy pricing.</p>
            <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Rate Table
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {rateTables.map((table) => (
              <Card key={table.id} className={`p-6 ${table.is_hidden ? 'opacity-60 border-dashed' : ''}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-bold text-white">{table.name}</h3>
                      {table.is_hidden && (
                        <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full flex items-center gap-1">
                          <EyeOff className="w-3 h-3" /> Hidden
                        </span>
                      )}
                    </div>
                    {table.notes && (
                      <p className="text-sm text-slate-400 mt-1">{table.notes}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-2">
                      Created {new Date(table.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/academy/rate-tables/${table.id}/config`}>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-blue-400 hover:bg-blue-500/10"
                      >
                        Configure
                      </Button>
                    </Link>
                    <Button 
                      onClick={() => openEditDialog(table)} 
                      variant="outline" 
                      size="sm"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      onClick={() => handleToggleHide(table.id, !table.is_hidden)} 
                      variant="outline" 
                      size="sm"
                      className={table.is_hidden ? "text-emerald-400 hover:bg-emerald-500/10" : "text-amber-400 hover:bg-amber-500/10"}
                      title={table.is_hidden ? "Show this rate table" : "Hide from registration"}
                    >
                      {table.is_hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button 
                      onClick={() => handleDelete(table.id)} 
                      variant="outline" 
                      size="sm"
                      className="text-red-400 hover:bg-red-500/10"
                      title="Delete rate table"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Discount Summary */}
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="bg-purple-500/10 p-3 rounded">
                    <h4 className="font-semibold text-purple-300 mb-2">Membership Discounts</h4>
                    <ul className="space-y-1 text-purple-400">
                      <li>Club Member: {table.membership_discounts?.club_member?.value > 0 ? `${table.membership_discounts.club_member.value}%` : <span className="text-slate-400">Disabled</span>}</li>
                      <li>Football Section Member: {table.membership_discounts?.section_member?.value > 0 ? `${table.membership_discounts.section_member.value}%` : <span className="text-slate-400">Disabled</span>}</li>
                      <li>Digital Member: {table.membership_discounts?.digital_member?.value > 0 ? `${table.membership_discounts.digital_member.value}%` : <span className="text-slate-400">Disabled</span>}</li>
                    </ul>
                    <div className="mt-2 pt-2 border-t border-purple-500/30">
                      <span className="text-xs text-purple-400">
                        Stacking: {
                          table.discount_stacking_mode === "club_digital_stack" ? "Club + Digital" :
                          table.discount_stacking_mode === "full_stack" ? "Full Stack" :
                          "Highest Only"
                        }
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-emerald-500/10 p-3 rounded">
                    <h4 className="font-semibold text-green-300 mb-2">Sibling Discounts</h4>
                    {(table.sibling_discounts || []).length > 0 ? (
                      <ul className="space-y-1 text-emerald-400">
                        {(table.sibling_discounts || []).map((d, idx) => (
                          <li key={idx}>{d.child_number}{d.child_number === 2 ? 'nd' : 'rd'}+ child: {d.value}%</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-emerald-400 text-xs">No sibling discounts</p>
                    )}
                  </div>
                  
                  <div className="bg-orange-500/10 p-3 rounded">
                    <h4 className="font-semibold text-orange-300 mb-2">
                      {table.pricing_mode === "volume"
                        ? "Volume Pricing"
                        : table.pricing_mode === "max_capacity"
                        ? "Fixed Term (Max Capacity)"
                        : "Session Discounts"}
                    </h4>
                    {table.pricing_mode === "volume" ? (
                      table.volume_pricing_tiers?.length > 0 ? (
                        <ul className="space-y-1 text-orange-400">
                          {table.volume_pricing_tiers.map((tier, idx) => (
                            <li key={idx}>
                              {tier.min_sessions}{tier.min_sessions >= 3 ? "+" : ""} session{tier.min_sessions === 1 ? "" : "s"}/wk: ${Number(tier.price_per_session).toFixed(2)}/session
                            </li>
                          ))}
                          <li className="text-orange-300/70 text-[11px] pt-1">
                            Member discount applies on top of these rates.
                          </li>
                        </ul>
                      ) : (
                        <p className="text-orange-400 text-xs">No volume tiers configured</p>
                      )
                    ) : table.pricing_mode === "max_capacity" ? (
                      table.max_capacity_price_per_session && table.max_sessions_per_week ? (
                        <ul className="space-y-1 text-orange-400">
                          <li>
                            ${Number(table.max_capacity_price_per_session).toFixed(2)}/session × {table.max_sessions_per_week} sessions/week
                          </li>
                          <li className="text-orange-300/70 text-[11px] pt-1">
                            Same total whether parent picks 1, 2, or {table.max_sessions_per_week} sessions. Member and sibling discounts apply on top.
                          </li>
                        </ul>
                      ) : (
                        <p className="text-orange-400 text-xs">Fixed term rate not configured</p>
                      )
                    ) : (
                      (table.session_count_discounts || []).length > 0 ? (
                        <ul className="space-y-1 text-orange-400">
                          {table.session_count_discounts.map((d, idx) => (
                            <li key={idx}>{d.session_count}{d.session_count === 2 ? 'nd' : 'rd'}+ session: {d.value}%</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-orange-400 text-xs">No session discounts</p>
                      )
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTable ? "Edit Rate Table" : "Create Rate Table"}</DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div>
                <label className="block text-sm font-semibold mb-2">Rate Table Name *</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Dempsey Premium Rates 2026"
                  className="mb-2"
                />
                <label className="block text-sm font-semibold mb-2">Notes / Description</label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional: Add notes for future reference (e.g., 'Higher facility costs, includes equipment')"
                  rows={2}
                />
              </div>

              {/* Membership Discounts */}
              <div className="border-t pt-4">
                <h3 className="font-bold text-lg mb-3">Membership Discounts (% off)</h3>
                <p className="text-sm text-slate-400 mb-3">Toggle to enable discounts for each membership tier</p>
                <div className="space-y-4">
                  {/* Club Member */}
                  <div className="flex items-center gap-4 p-3 bg-[#0a0f1a] rounded-lg">
                    <Switch
                      checked={clubMemberEnabled}
                      onCheckedChange={setClubMemberEnabled}
                    />
                    <div className="flex-1">
                      <label className="block text-sm font-medium">Club Member (SCC)</label>
                      <p className="text-xs text-slate-400">SCC members only</p>
                    </div>
                    {clubMemberEnabled && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={clubMemberValue}
                          onChange={(e) => setClubMemberValue(e.target.value)}
                          placeholder="15"
                          min="0"
                          max="100"
                          className="w-24"
                        />
                        <span className="text-sm text-slate-400">%</span>
                      </div>
                    )}
                    {!clubMemberEnabled && (
                      <span className="text-sm text-slate-400">Disabled</span>
                    )}
                  </div>
                  
                  {/* Section Member */}
                  <div className="flex items-center gap-4 p-3 bg-[#0a0f1a] rounded-lg">
                    <Switch
                      checked={sectionMemberEnabled}
                      onCheckedChange={setSectionMemberEnabled}
                    />
                    <div className="flex-1">
                      <label className="block text-sm font-medium">Football Section Member</label>
                      <p className="text-xs text-slate-400">SCC Football Section members</p>
                    </div>
                    {sectionMemberEnabled && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={sectionMemberValue}
                          onChange={(e) => setSectionMemberValue(e.target.value)}
                          placeholder="20"
                          min="0"
                          max="100"
                          className="w-24"
                        />
                        <span className="text-sm text-slate-400">%</span>
                      </div>
                    )}
                    {!sectionMemberEnabled && (
                      <span className="text-sm text-slate-400">Disabled</span>
                    )}
                  </div>
                  
                  {/* Digital Member */}
                  <div className="flex items-center gap-4 p-3 bg-[#0a0f1a] rounded-lg">
                    <Switch
                      checked={digitalMemberEnabled}
                      onCheckedChange={setDigitalMemberEnabled}
                    />
                    <div className="flex-1">
                      <label className="block text-sm font-medium">Digital Member</label>
                      <p className="text-xs text-slate-400">Open to all (non-section members)</p>
                    </div>
                    {digitalMemberEnabled && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={digitalMemberValue}
                          onChange={(e) => setDigitalMemberValue(e.target.value)}
                          placeholder="8"
                          min="0"
                          max="100"
                          className="w-24"
                        />
                        <span className="text-sm text-slate-400">%</span>
                      </div>
                    )}
                    {!digitalMemberEnabled && (
                      <span className="text-sm text-slate-400">Disabled</span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2">Non-members automatically get 0% discount</p>
                
                {/* Discount Stacking Mode */}
                <div className="mt-4 p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                  <h4 className="font-semibold text-blue-300 mb-2">Discount Stacking Mode</h4>
                  <p className="text-sm text-blue-400 mb-3">How should membership discounts combine?</p>
                  <div className="space-y-2">
                    <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-blue-500/20">
                      <input
                        type="radio"
                        name="stackingMode"
                        value="highest_only"
                        checked={discountStackingMode === "highest_only"}
                        onChange={(e) => setDiscountStackingMode(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <span className="font-medium">Highest Only</span>
                        <p className="text-xs text-slate-400">Member gets their single highest discount (no stacking)</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-blue-500/20">
                      <input
                        type="radio"
                        name="stackingMode"
                        value="club_digital_stack"
                        checked={discountStackingMode === "club_digital_stack"}
                        onChange={(e) => setDiscountStackingMode(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <span className="font-medium">Club + Digital Stack</span>
                        <p className="text-xs text-slate-400">Section members get section rate. Club members can add Digital for combined discount.</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-blue-500/20">
                      <input
                        type="radio"
                        name="stackingMode"
                        value="full_stack"
                        checked={discountStackingMode === "full_stack"}
                        onChange={(e) => setDiscountStackingMode(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <span className="font-medium">Full Stack</span>
                        <p className="text-xs text-slate-400">All applicable discounts combine (Section + Club + Digital)</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Sibling Discounts */}
              <div className="border-t pt-4">
                <h3 className="font-bold text-lg mb-3">Sibling Discounts (% off total)</h3>
                <p className="text-sm text-slate-400 mb-3">Toggle to enable discount for multiple children</p>
                <div className="space-y-3">
                  {siblingDiscounts.map((discount, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-[#0a0f1a] rounded-lg">
                      <Switch
                        checked={discount.enabled}
                        onCheckedChange={(checked) => updateSiblingDiscount(idx, 'enabled', checked)}
                      />
                      <div className="flex-1">
                        <label className="text-sm font-medium">
                          {discount.child_number === 2 ? '2nd Child' : '3rd+ Child'}
                        </label>
                      </div>
                      {discount.enabled && (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={discount.value}
                            onChange={(e) => updateSiblingDiscount(idx, 'value', e.target.value)}
                            placeholder="10"
                            min="0"
                            max="100"
                            className="w-24"
                          />
                          <span className="text-sm text-slate-400">%</span>
                        </div>
                      )}
                      {!discount.enabled && (
                        <span className="text-sm text-slate-400">Disabled</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Session Pricing Configuration */}
              <div className="border-t pt-4">
                <h3 className="font-bold text-lg mb-3">Session Pricing Mode</h3>
                
                {/* Pricing Mode Toggle */}
                <div className="flex gap-2 mb-4 flex-wrap">
                  <Button
                    type="button"
                    variant={pricingMode === "progressive" ? "default" : "outline"}
                    onClick={() => setPricingMode("progressive")}
                    className={pricingMode === "progressive" ? "bg-blue-600" : ""}
                  >
                    Progressive Discounts
                  </Button>
                  <Button
                    type="button"
                    variant={pricingMode === "volume" ? "default" : "outline"}
                    onClick={() => setPricingMode("volume")}
                    className={pricingMode === "volume" ? "bg-blue-600" : ""}
                  >
                    Volume-Based Pricing
                  </Button>
                  <Button
                    type="button"
                    variant={pricingMode === "max_capacity" ? "default" : "outline"}
                    onClick={() => setPricingMode("max_capacity")}
                    className={pricingMode === "max_capacity" ? "bg-amber-600 hover:bg-amber-500" : ""}
                    data-testid="pricing-mode-max-capacity-new"
                  >
                    Fixed Term (Max Capacity)
                  </Button>
                </div>

                {/* Progressive Mode - Session Count Discounts */}
                {pricingMode === "progressive" && (
                  <div className="bg-[#0a0f1a] p-4 rounded-lg">
                    <p className="text-sm text-slate-400 mb-3">
                      Each additional session gets a progressive discount off the session&apos;s base price.
                    </p>
                    <div className="space-y-3">
                      {sessionDiscounts.map((discount, idx) => {
                        const ordinals = { 2: '2nd', 3: '3rd', 4: '4th', 5: '5th' };
                        const label = ordinals[discount.session_count] || `${discount.session_count}th`;
                        return (
                          <div key={idx} className="flex items-center gap-4 p-3 bg-slate-900/50 rounded-lg border">
                            <Switch
                              checked={discount.enabled}
                              onCheckedChange={(checked) => updateSessionDiscount(idx, 'enabled', checked)}
                            />
                            <div className="flex-1">
                              <label className="text-sm font-medium">
                                {label} Session
                              </label>
                            </div>
                            {discount.enabled && (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  value={discount.value}
                                  onChange={(e) => updateSessionDiscount(idx, 'value', e.target.value)}
                                  placeholder="5"
                                  min="0"
                                  max="100"
                                  className="w-24"
                                />
                                <span className="text-sm text-slate-400">% off</span>
                              </div>
                            )}
                            {!discount.enabled && (
                              <span className="text-sm text-slate-400">Disabled</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      Example: If 1st session is $45, 2nd at 10% off = $40.50, 3rd at 20% off = $36
                    </p>
                  </div>
                )}

                {/* Volume Mode - Price Per Session Tiers */}
                {pricingMode === "volume" && (
                  <div className="bg-emerald-500/10 p-4 rounded-lg">
                    <p className="text-sm text-slate-400 mb-3">
                      Enter the <strong>non-member (base) price</strong> per session. Members automatically get the configured discount (e.g., 10% off).
                    </p>
                    {volumePricingTiers.map((tier, idx) => (
                      <div key={idx} className="flex items-center gap-3 mb-3">
                        <label className="text-sm font-medium w-32">
                          {tier.min_sessions === 1 ? "1 session/week" : 
                           tier.min_sessions === 2 ? "2 sessions/week" : 
                           `${tier.min_sessions}+ sessions/week`}:
                        </label>
                        <span className="text-sm text-slate-400">$</span>
                        <Input
                          type="number"
                          value={tier.price_per_session}
                          onChange={(e) => {
                            const newTiers = [...volumePricingTiers];
                            newTiers[idx].price_per_session = e.target.value;
                            setVolumePricingTiers(newTiers);
                          }}
                          placeholder="e.g., 35.56"
                          min="0"
                          step="0.01"
                          className="flex-1"
                        />
                        <span className="text-sm text-slate-400">/session</span>
                      </div>
                    ))}
                    <p className="text-xs text-slate-400 mt-2">
                      Example: Non-member pays $35.56/session. Member with 10% discount pays $32/session.
                    </p>
                    
                    {/* Add/Remove Tier Buttons */}
                    <div className="flex gap-2 mt-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const nextMin = volumePricingTiers.length + 1;
                          setVolumePricingTiers([...volumePricingTiers, { min_sessions: nextMin, price_per_session: "" }]);
                        }}
                      >
                        + Add Tier
                      </Button>
                      {volumePricingTiers.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setVolumePricingTiers(volumePricingTiers.slice(0, -1))}
                        >
                          Remove Last
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Max Capacity (Fixed Term) Mode — Jun 2026 */}
                {pricingMode === "max_capacity" && (
                  <div className="bg-amber-500/10 p-4 rounded-lg" data-testid="max-capacity-config-new">
                    <p className="text-sm text-amber-200 mb-3">
                      <strong>Fixed Term (Max Capacity):</strong> Parents always pay for the maximum sessions/week regardless of how many they pick.
                      Prevents &quot;book 1, attend 3&quot; freeloading. Formula:
                      <em> price/session × max sessions/week × term weeks</em>. Member and sibling discounts still apply on top; pro-rata for late
                      registrations still applies.
                    </p>

                    <div className="grid md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Price per Session ($)</label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={maxCapacityPricePerSession}
                          onChange={(e) => setMaxCapacityPricePerSession(e.target.value)}
                          placeholder="e.g., 30"
                          data-testid="max-capacity-price-per-session-new"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Max Sessions per Week</label>
                        <Input
                          type="number"
                          min="1"
                          max="7"
                          value={maxSessionsPerWeek}
                          onChange={(e) => setMaxSessionsPerWeek(e.target.value)}
                          placeholder="e.g., 3"
                          data-testid="max-capacity-max-sessions-per-week-new"
                        />
                      </div>
                    </div>

                    <div className="mt-2 p-3 bg-slate-900/50 rounded-lg border border-white/10">
                      <p className="text-sm text-white">
                        <span className="text-slate-400">Term fee preview (non-member, 10 weeks):</span>{" "}
                        <span className="font-bold text-amber-300" data-testid="max-capacity-preview-total-new">
                          ${(
                            parseFloat(maxCapacityPricePerSession || 0) *
                            parseInt(maxSessionsPerWeek || 0) *
                            10
                          ).toFixed(2)}
                        </span>
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        = ${maxCapacityPricePerSession || 0}/session × {maxSessionsPerWeek || 0} sessions/week × 10 weeks
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Same price whether the parent picks 1, 2, or {maxSessionsPerWeek || 0} sessions per week.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button onClick={() => setShowDialog(false)} variant="outline">
                  Cancel
                </Button>
                <Button onClick={() => handleSubmit(false)} className="bg-blue-600 hover:bg-blue-700">
                  {editingTable ? "Update Rate Table" : "Create Rate Table"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Warning Dialog for editing locked rate tables */}
        <AlertDialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
          <AlertDialogContent className="bg-slate-900 border-amber-500/50">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-amber-400">
                <AlertTriangle className="w-5 h-5" />
                Rate Table In Use
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-300">
                {warningMessage}
                <p className="mt-2 text-amber-300/80 text-sm">
                  Do you want to proceed with editing this rate table?
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                onClick={() => {
                  setShowWarningDialog(false);
                  setPendingPayload(null);
                }}
                className="bg-slate-700 text-white hover:bg-slate-600"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmForceEdit}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Yes, Edit Anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
